/**
 * Procedural effect voices (Web Audio, no assets): teletype clicks, rubber-stamp thump, telegraph
 * sounder, teletype bell, paper swish — and the signals traffic of the war room: morse chirps for
 * orders going out and acknowledgements coming back, the magneto ring of a field telephone and
 * radio static. Deliberately quiet — the war room is a quiet place.
 *
 * Anything can request a sound with `bus.emit('sound', { name })`; see `initAudio()` in index.ts.
 */
import { bus } from '../game/bus';
import { getMixer, isAudible, type Mixer } from './mixer';

export type SoundName =
  | 'click'
  | 'type'
  | 'stamp'
  | 'alert'
  | 'bell'
  | 'paper'
  | 'confirm'
  | 'deny'
  | 'morse'
  | 'morseOut'
  | 'phone'
  | 'static';

/** Gain envelope (exponential attack/decay) feeding `dest`. */
export function env(
  c: BaseAudioContext,
  dest: AudioNode,
  peak: number,
  attack: number,
  decay: number,
  at = c.currentTime,
): GainNode {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), at + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
  g.connect(dest);
  return g;
}

/** Filtered burst from the shared white-noise buffer. Returns the filter for extra automation. */
export function noiseBurst(
  m: Mixer,
  dest: AudioNode,
  filter: BiquadFilterType,
  freq: number,
  q: number,
  peak: number,
  decay: number,
  at = m.ctx.currentTime,
): BiquadFilterNode {
  const c = m.ctx;
  const src = c.createBufferSource();
  src.buffer = m.white;
  src.playbackRate.value = 0.8 + Math.random() * 0.4;
  const f = c.createBiquadFilter();
  f.type = filter;
  f.frequency.value = freq;
  f.Q.value = q;
  src.connect(f).connect(env(c, dest, peak, 0.002, decay, at));
  src.start(at, Math.random() * 0.5, decay + 0.05);
  return f;
}

export function tone(
  c: BaseAudioContext,
  dest: AudioNode,
  type: OscillatorType,
  from: number,
  to: number,
  peak: number,
  decay: number,
  at = c.currentTime,
  attack = 0.003,
): OscillatorNode {
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(from, at);
  if (to !== from) o.frequency.exponentialRampToValueAtTime(to, at + attack + decay);
  o.connect(env(c, dest, peak, attack, decay, at));
  o.start(at);
  o.stop(at + attack + decay + 0.05);
  return o;
}

/** Morse letters used for the chatter (R = received, K = go ahead …) — short and friendly. */
const MORSE = ['.-.', '-.-', '..-.', '.-..', '-..', '...-', '--.'];

/** Soft sine keyed in a morse pattern; a faint key click marks each key-down. */
function keyed(m: Mixer, freq: number, dot: number, peak: number): void {
  const c = m.ctx;
  const pattern = MORSE[Math.floor(Math.random() * MORSE.length)];
  const band = c.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.value = freq;
  band.Q.value = 3;
  band.connect(m.fx);
  let t = c.currentTime + 0.01;
  for (const ch of pattern) {
    const len = ch === '-' ? dot * 3 : dot;
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.value = freq;
    const g = c.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.006);
    g.gain.setValueAtTime(peak, t + len - 0.008);
    g.gain.linearRampToValueAtTime(0, t + len);
    o.connect(g).connect(band);
    o.start(t);
    o.stop(t + len + 0.02);
    noiseBurst(m, m.fx, 'bandpass', 3000, 1.5, peak * 0.5, 0.006, t);
    t += len + dot;
  }
}

const PLAYERS: Record<SoundName, (m: Mixer) => void> = {
  click(m) {
    noiseBurst(m, m.fx, 'bandpass', 3200, 1.2, 0.18, 0.012);
    tone(m.ctx, m.fx, 'sine', 1900, 1500, 0.05, 0.02);
  },
  type(m) {
    noiseBurst(m, m.fx, 'bandpass', 2200 + Math.random() * 1400, 2, 0.09, 0.008 + Math.random() * 0.006);
  },
  stamp(m) {
    const t = m.ctx.currentTime;
    tone(m.ctx, m.fx, 'sine', 120, 42, 0.55, 0.2);
    noiseBurst(m, m.fx, 'lowpass', 700, 0.7, 0.35, 0.07);
    noiseBurst(m, m.fx, 'bandpass', 2400, 1, 0.08, 0.03, t + 0.012);
  },
  alert(m) {
    const c = m.ctx;
    const t = c.currentTime;
    for (const dt of [0, 0.11, 0.26]) {
      const o = c.createOscillator();
      o.type = 'square';
      o.frequency.value = 740;
      const f = c.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = 1600;
      o.connect(f).connect(env(c, m.fx, 0.06, 0.004, 0.07, t + dt));
      o.start(t + dt);
      o.stop(t + dt + 0.1);
    }
  },
  bell(m) {
    tone(m.ctx, m.fx, 'sine', 2093, 2093, 0.07, 0.9);
    tone(m.ctx, m.fx, 'sine', 3140, 3140, 0.025, 0.5);
    tone(m.ctx, m.fx, 'sine', 5230, 5230, 0.01, 0.25);
  },
  paper(m) {
    const f = noiseBurst(m, m.fx, 'bandpass', 900, 0.8, 0.07, 0.16);
    f.frequency.exponentialRampToValueAtTime(3200, m.ctx.currentTime + 0.15);
  },
  confirm(m) {
    tone(m.ctx, m.fx, 'sine', 880, 880, 0.05, 0.06);
    tone(m.ctx, m.fx, 'sine', 1320, 1320, 0.05, 0.09, m.ctx.currentTime + 0.07);
  },
  deny(m) {
    tone(m.ctx, m.fx, 'sawtooth', 150, 120, 0.04, 0.14);
  },
  /** Acknowledgement arriving on the sounder: lower, unhurried. */
  morse(m) {
    keyed(m, 610, 0.06, 0.045);
  },
  /** An order going out on the key: brighter and quicker. */
  morseOut(m) {
    keyed(m, 820, 0.045, 0.035);
  },
  /** Field-telephone magneto: two short buzzy rings through a small receiver. */
  phone(m) {
    const c = m.ctx;
    const t0 = c.currentTime;
    for (const dt of [0, 0.3]) {
      const t = t0 + dt;
      const o = c.createOscillator();
      o.type = 'square';
      o.frequency.value = 430;
      const trem = c.createOscillator();
      trem.frequency.value = 24;
      const depth = c.createGain();
      depth.gain.value = 0.5;
      const amp = c.createGain();
      amp.gain.value = 0.5;
      trem.connect(depth).connect(amp.gain);
      const lp = c.createBiquadFilter();
      lp.type = 'bandpass';
      lp.frequency.value = 1100;
      lp.Q.value = 0.9;
      o.connect(amp)
        .connect(lp)
        .connect(env(c, m.fx, 0.03, 0.01, 0.2, t));
      o.start(t);
      trem.start(t);
      o.stop(t + 0.25);
      trem.stop(t + 0.25);
    }
  },
  /** Radio: a breath of static with crackle and a heterodyne whistle as the set tunes in. */
  static(m) {
    const c = m.ctx;
    const t = c.currentTime;
    const f = noiseBurst(m, m.fx, 'bandpass', 1900, 0.6, 0.05, 0.42, t);
    f.frequency.linearRampToValueAtTime(1300, t + 0.4);
    for (let i = 0; i < 5; i++)
      noiseBurst(m, m.fx, 'highpass', 3500, 0.7, 0.03 + Math.random() * 0.03, 0.01, t + Math.random() * 0.4);
    tone(c, m.fx, 'sine', 1500, 950, 0.012, 0.3, t + 0.06, 0.05);
  },
};

/** Minimum ms between two plays of the same sound (bursts become a patter, not a roar). */
const MIN_GAP: Partial<Record<SoundName, number>> = {
  type: 38,
  click: 30,
  morse: 520,
  morseOut: 380,
  phone: 700,
  static: 420,
  paper: 60,
};
const lastPlayed = new Map<SoundName, number>();

/** Play an effect by name at a relative volume (0..1). No-op until audio is unlocked. */
export function playSound(name: string, volume = 1): void {
  const player = PLAYERS[name as SoundName];
  const m = getMixer();
  if (!player || !m || !isAudible() || volume <= 0.001) return;
  const now = performance.now();
  const gap = MIN_GAP[name as SoundName] ?? 0;
  if (now - (lastPlayed.get(name as SoundName) ?? -1e9) < gap) return;
  lastPlayed.set(name as SoundName, now);
  if (volume >= 0.999) {
    player(m);
    return;
  }
  // Scale one play without touching the shared bus: route through a temporary gain.
  const g = m.ctx.createGain();
  g.gain.value = volume;
  g.connect(m.fx);
  player({ ...m, fx: g });
  window.setTimeout(() => g.disconnect(), 3000);
}

/** Route bus 'sound' requests to the voices. */
export function listenForSounds(): () => void {
  return bus.on('sound', ({ name, volume }) => playSound(name, volume));
}
